<template>
  <footer class="mt-auto border-t border-base-300/60 bg-base-100">
    <div class="container mx-auto px-4 lg:px-6 py-10">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">
        <!-- Brand -->
        <div class="sm:col-span-2 lg:col-span-1 space-y-4">
          <div class="flex items-center gap-2.5">
            <img
              src="/logo.jpg"
              alt="ilterX"
              class="h-8 w-auto object-contain"
            />
          </div>
          <p class="text-sm text-base-content/60 leading-relaxed max-w-xs">
            {{ t('footer.tagline') }}
          </p>
          <div class="flex gap-2">
            <a
              v-for="social in socialLinks"
              :key="social.label"
              :href="social.href"
              :aria-label="social.label"
              class="btn btn-ghost btn-sm btn-square hover:bg-primary/10 hover:text-primary"
            >
              <span v-html="social.icon"></span>
            </a>
          </div>
        </div>

        <!-- Product -->
        <div>
          <h3 class="text-sm font-semibold mb-3">{{ t('footer.product') }}</h3>
          <ul class="space-y-2">
            <li v-for="link in productLinks" :key="link.to">
              <router-link
                :to="link.to"
                class="text-sm text-base-content/60 hover:text-primary transition-colors inline-flex items-center gap-1.5"
              >
                {{ link.label }}
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Legal -->
        <div>
          <h3 class="text-sm font-semibold mb-3">{{ t('footer.legal') }}</h3>
          <ul class="space-y-2">
            <li v-for="link in legalLinks" :key="link.to">
              <router-link
                :to="link.to"
                class="text-sm text-base-content/60 hover:text-primary transition-colors"
              >
                {{ link.label }}
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Theme & Language -->
        <div class="space-y-4">
          <ThemeToggle />
          <div>
            <h3 class="text-sm font-semibold mb-3">{{ t('footer.language') }}</h3>
            <div class="join">
              <button
                class="btn btn-sm join-item"
                :class="currentLocale === 'tr' ? 'btn-primary' : 'btn-ghost'"
                @click="setLocale('tr')"
              >
                TR
              </button>
              <button
                class="btn btn-sm join-item"
                :class="currentLocale === 'en' ? 'btn-primary' : 'btn-ghost'"
                @click="setLocale('en')"
              >
                EN
              </button>
            </div>
          </div>
        </div>

        <!-- Contact -->
        <div>
          <h3 class="text-sm font-semibold mb-3">{{ t('footer.contact') }}</h3>
          <ul class="space-y-2.5 text-sm text-base-content/60">
            <li class="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 shrink-0 text-primary/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              support@todoapp.com
            </li>
            <li class="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 shrink-0 text-primary/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              (123) 456-7890
            </li>
          </ul>
          <button
            type="button"
            class="btn btn-outline btn-sm mt-4 w-full sm:w-auto"
            @click="openContactModal"
          >
            {{ t('footer.contactUs') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Bottom bar -->
    <div class="border-t border-base-300/60">
      <div class="container mx-auto px-4 lg:px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-base-content/50">
        <p>{{ t('footer.copyright', { year: currentYear, siteName: t('common.siteName') }) }}</p>
        <div class="flex items-center gap-4">
          <router-link to="/privacy" class="hover:text-primary transition-colors">{{ t('footer.links.privacy') }}</router-link>
          <router-link to="/tos" class="hover:text-primary transition-colors">{{ t('footer.links.terms') }}</router-link>
          <router-link to="/cookie-policy" class="hover:text-primary transition-colors">{{ t('footer.links.cookies') }}</router-link>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { computed } from 'vue';
import ThemeToggle from '../ThemeToggle.vue';
import useLocale from '../../compositions/useLocale';
import useContactModal from '../../compositions/useContactModal';

const { t, currentLocale, setLocale } = useLocale();
const { openContactModal } = useContactModal();
const currentYear = new Date().getFullYear();

const productLinks = computed(() => [
  { to: '/', label: t('footer.links.home') },
  { to: '/tasks', label: 'Görevler' },
  { to: '/calendar', label: 'Takvim' },
]);

const legalLinks = computed(() => [
  { to: '/tos', label: t('footer.links.termsOfService') },
  { to: '/privacy', label: t('footer.links.privacyPolicy') },
  { to: '/cookie-policy', label: t('footer.links.cookiePolicy') },
]);

const socialLinks = computed(() => [
  {
    label: t('common.social.twitter'),
    href: '#',
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" class="fill-current"><path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"></path></svg>`,
  },
  {
    label: t('common.social.youtube'),
    href: '#',
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" class="fill-current"><path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"></path></svg>`,
  },
  {
    label: t('common.social.facebook'),
    href: '#',
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" class="fill-current"><path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z"></path></svg>`,
  },
]);
</script>
